// ============================================================================
// BESPOKIAN Health - HIPAA Notice Page
// Legal page with luxury styling
// ============================================================================

import { Link } from '@tanstack/react-router';
import { Navigation } from '@/components/layout/Navigation';
import { Footer } from '@/components/layout/Footer';
import { Shield, FileText, Users, Lock, Bell, Scale, Mail, Phone, MapPin, ChevronRight } from 'lucide-react';

const sections = [
  {
    icon: FileText,
    title: 'Uses and Disclosures of PHI',
    content: `We may use and disclose your Protected Health Information (PHI) for:
• Treatment: Filling prescriptions, coordinating care with providers
• Payment: Processing insurance claims, collecting payment
• Healthcare Operations: Quality improvement, training, auditing
• As Required by Law: Court orders, public health reporting
• With Your Authorization: Any other uses require your written consent`,
  },
  {
    icon: Shield,
    title: 'Your Rights',
    content: `Under HIPAA, you have the right to:
• Access your PHI and obtain copies
• Request amendments to your health information
• Receive an accounting of disclosures
• Request restrictions on certain uses
• Request confidential communications
• File a complaint if you believe your rights were violated`,
  },
  {
    icon: Lock,
    title: 'Our Responsibilities',
    content: `BESPOKIAN Health is required to:
• Maintain the privacy of your PHI
• Provide you with this notice of our privacy practices
• Follow the terms of this notice currently in effect
• Notify you if a breach of your unsecured PHI occurs`,
  },
  {
    icon: Users,
    title: 'Minimum Necessary Standard',
    content: `We limit PHI use and disclosure to the minimum necessary to accomplish the intended purpose. Our workforce members only access PHI needed for their job functions. Business associates are contractually required to protect your information.`,
  },
  {
    icon: Bell,
    title: 'Communication Preferences',
    content: `You may request that we communicate with you in a specific way or at a specific location. For example, you may ask that we contact you only at work or by mail. We will accommodate reasonable requests.`,
  },
  {
    icon: Scale,
    title: 'Changes to This Notice',
    content: `We reserve the right to change this notice and make the new provisions effective for all PHI we maintain. Current notices are available on our website and upon request.`,
  },
];

export function HIPAAPage() {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navigation />

      <main className="flex-1 pt-24">
        {/* Hero */}
        <section className="bg-gradient-to-br from-navy-50 via-white to-sage-50/30 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="w-16 h-0.5 bg-gold-500 mx-auto mb-6" />

            <p className="text-gold-600 font-medium tracking-wide uppercase text-sm mb-4">
              Legal
            </p>

            <h1 className="font-display text-4xl md:text-5xl text-navy-800 tracking-tight leading-tight mb-6 italic">
              HIPAA Notice of Privacy Practices
            </h1>

            <p className="text-xl text-navy-600 max-w-2xl mx-auto">
              This notice describes how medical information about you may be used and disclosed 
              and how you can get access to this information.
            </p>

            <p className="text-sm text-navy-400 mt-4">Effective Date: January 2026</p>
          </div>
        </section>

        {/* Content */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* CTA: HIPAA compliance roadmap */}
            <Link
              to="/hipaa-tracking"
              className="mb-8 flex items-center justify-between rounded-xl border border-navy-200 bg-navy-50 px-5 py-4 text-navy-800 transition-colors hover:border-gold-400 hover:bg-navy-50/80"
            >
              <span className="font-medium">View our HIPAA compliance roadmap</span>
              <ChevronRight className="h-5 w-5 text-gold-600" />
            </Link>

            {/* Commitment Banner */}
            <div className="bg-sage-50 rounded-xl p-6 mb-10 border border-sage-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-sage-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Shield className="h-6 w-6 text-sage-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-navy-800 mb-2">Our Commitment to Your Privacy</h2>
                  <p className="text-navy-600">
                    BESPOKIAN Health is committed to protecting your health information. We are required by law 
                    to maintain the privacy of your Protected Health Information (PHI), provide you with this notice, 
                    and follow the terms of this notice.
                  </p>
                </div>
              </div>
            </div>

            {/* Sections */}
            <div className="space-y-6">
              {sections.map((section) => (
                <div key={section.title} className="bg-navy-50 rounded-xl p-6 border border-navy-100">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gold-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <section.icon className="h-6 w-6 text-gold-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-navy-800 mb-3">{section.title}</h2>
                      <p className="text-navy-600 whitespace-pre-line text-sm leading-relaxed">{section.content}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Contact Section */}
            <div className="mt-10 p-6 bg-gold-50 rounded-xl border border-gold-200">
              <h3 className="font-semibold text-navy-800 mb-4">Contact Our Privacy Officer</h3>
              <p className="text-navy-600 mb-4">
                For questions about this notice or to exercise your rights:
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-navy-600">
                  <Mail className="h-4 w-4 text-gold-600" />
                  <a href="mailto:privacy@bespokianhealth.com" className="text-gold-600 hover:text-gold-700 font-medium">
                    privacy@bespokianhealth.com
                  </a>
                </div>
                <div className="flex items-center gap-3 text-navy-600">
                  <Phone className="h-4 w-4 text-gold-600" />
                  <span>1-877-BES-PORX</span>
                </div>
                <div className="flex items-center gap-3 text-navy-600">
                  <MapPin className="h-4 w-4 text-gold-600" />
                  <span>BESPOKIAN Health, Attn: Privacy Officer, 123 Pharmacy Way, Altamonte Springs, FL</span>
                </div>
              </div>
              <p className="text-navy-500 text-sm mt-4">
                To file a complaint with the Department of Health and Human Services, visit{' '}
                <a 
                  href="https://www.hhs.gov/hipaa/filing-a-complaint" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-gold-600 hover:text-gold-700"
                >
                  hhs.gov/hipaa/filing-a-complaint
                </a>
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}



